import { useMemo } from 'react';
import { groupBy } from 'lodash';
import { useSubscription } from '@apollo/client';
import { useInstallationContext } from '../../../../../../context/installation';
import {
  MeasurementChannelKind,
  MeasurementSubscriptionResponse,
  OnDetailsMeasurementSubscription,
  OnDetailsMeasurementSubscriptionVariables,
  OptimizerVectorParameterTypeInternal,
} from '../../../../../../data-access/gql-types/graphql';
import { ON_DETAILS_MEASUREMENT } from '../../../../../../data-access/subscriptions/on-measurement';
import { useMeasurementMapOperations } from '../../../../hooks/measurement-map/use-measurement-map-operations';
import { ChannelType } from '../../../../types/channel/base';
import { ChannelOptimizerType } from '../../../../types/channel/optimizer';
import { OptimizerCurrentDataMap } from '../current/types';

export const useOptimizerMeasurement = (channel: ChannelType) => {
  const { skipLavvaFetch } = useInstallationContext();
  const { convertNumberToMeasurement } = useMeasurementMapOperations();
  const supportedScalars = (channel.data as ChannelOptimizerType).supportedScalarParameters;
  const supportedVectors = (channel.data as ChannelOptimizerType).supportedVectorParameters;

  const { data } = useSubscription<OnDetailsMeasurementSubscription, OnDetailsMeasurementSubscriptionVariables>(
    ON_DETAILS_MEASUREMENT,
    {
      variables: {
        input: {
          channelId: channel?.id,
          deviceId: channel?.deviceId,
          kind: MeasurementChannelKind.Optimizer,
          scalarParameterTypes: supportedScalars,
          vectorParameterTypes: supportedVectors,
          indices: [],
        },
      },
      skip: !channel || skipLavvaFetch,
    },
  );

  const optimizerData = useMemo(() => {
    if ((data?.onDetailsMeasurement.data as MeasurementSubscriptionResponse)?.vectorMeasurements) {
      const mapData = (data?.onDetailsMeasurement.data as MeasurementSubscriptionResponse).vectorMeasurements.map(
        (x) => ({
          index: x.index,
          value: x.value,
          type: convertNumberToMeasurement(MeasurementChannelKind.Optimizer)('vectors', x.type),
        }),
      );

      return groupBy(mapData, (x) => x.type) as Record<OptimizerVectorParameterTypeInternal, OptimizerCurrentDataMap[]>;
    }
  }, [data]);

  return {
    optimizerData,
    measurementData: data?.onDetailsMeasurement.data as MeasurementSubscriptionResponse | undefined,
  };
};
